import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Alert,
  LinearProgress,
  Autocomplete,
  Tabs,
  Tab
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import api from '../services/api';
import { useDateFormat } from '../hooks/useDateFormat';
import { getTimezoneDisplayName, getUserTimezone } from '../utils/dateUtils';
import { formatCurrencyPickerLabel, getIso4217CurrencyCodes } from '../utils/iso4217';
import UserWorkingCalendarPanel from './UserWorkingCalendarPanel';

function pickEffectiveOrgDefault(
  rows: Array<{ kind: string; effectiveFrom: string; amount: string; currencyCode: string }>,
  kind: 'EMPLOYEE_COST' | 'BILLING',
  at: Date
): { amount: string; currencyCode: string; effectiveFrom: string } | null {
  const eligible = rows.filter(
    (r) => r.kind === kind && new Date(r.effectiveFrom).getTime() <= at.getTime()
  );
  eligible.sort((a, b) => new Date(b.effectiveFrom).getTime() - new Date(a.effectiveFrom).getTime());
  return eligible[0] ?? null;
}

export type OrgMemberEditUser = {
  id: string;
  email: string;
  displayName?: string | null;
  organizationRole?: string;
  organizationMemberActive?: boolean;
};

type HourlyRow = {
  id: string;
  kind: 'EMPLOYEE_COST' | 'BILLING';
  effectiveFrom: string;
  amount: string;
  currencyCode: string;
};

type OrgDefaultRow = {
  kind: 'EMPLOYEE_COST' | 'BILLING';
  effectiveFrom: string;
  amount: string;
  currencyCode: string;
};

type Props = {
  open: boolean;
  onClose: () => void;
  organizationId: string;
  user: OrgMemberEditUser | null;
  availableRoles: string[];
  onSaved: () => void;
};

const OrganizationMemberEditDialog: React.FC<Props> = ({
  open,
  onClose,
  organizationId,
  user,
  availableRoles,
  onSaved
}) => {
  const currencyCodes = useMemo(() => getIso4217CurrencyCodes(), []);
  const { formatDateTime, toDateTimeLocal, fromDateTimeLocal, preferences } = useDateFormat();
  const userTz = getUserTimezone(preferences);
  const userTzLabel = getTimezoneDisplayName(userTz);
  const timesShownInUtcOnly = userTz === 'UTC' || userTz === 'Etc/UTC';
  const dateTimeFieldHelperText = timesShownInUtcOnly
    ? 'Stored as UTC. Your User preferences use UTC, so this matches server storage.'
    : `Interpreted in your User preference timezone (${userTzLabel}), then saved as UTC.`;
  const [tab, setTab] = useState(0);
  const [role, setRole] = useState('MEMBER');
  const [memberActive, setMemberActive] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [hourlyLoading, setHourlyLoading] = useState(false);
  const [hourlyRows, setHourlyRows] = useState<HourlyRow[]>([]);
  const [hourlyDialogOpen, setHourlyDialogOpen] = useState(false);
  const [hourlyDialogKind, setHourlyDialogKind] = useState<'EMPLOYEE_COST' | 'BILLING'>('EMPLOYEE_COST');
  const [hourlyEditId, setHourlyEditId] = useState<string | null>(null);
  const [hourlyFormFrom, setHourlyFormFrom] = useState('');
  const [hourlyFormAmount, setHourlyFormAmount] = useState('');
  const [hourlyFormCurrency, setHourlyFormCurrency] = useState('USD');
  const [orgDefaultRows, setOrgDefaultRows] = useState<OrgDefaultRow[]>([]);

  useEffect(() => {
    if (open && user) {
      setTab(0);
      setRole((user.organizationRole || 'MEMBER').toUpperCase());
      setMemberActive(user.organizationMemberActive !== false);
      setError(null);
      setSuccess(null);
    }
  }, [open, user]);

  const loadHourlyData = useCallback(async () => {
    if (!organizationId || !user?.id) return;
    setHourlyLoading(true);
    try {
      const [memResult, orgResult] = await Promise.allSettled([
        api.getMemberHourlyRates(organizationId, user.id),
        api.getOrganizationHourlyRates(organizationId)
      ]);

      if (memResult.status === 'fulfilled') {
        const memRes = memResult.value;
        const memData = memRes?.data ?? memRes;
        const memList = Array.isArray(memData) ? memData : [];
        setHourlyRows(
          memList.map((r: Record<string, unknown>) => ({
            id: String(r.id),
            kind: r.kind as 'EMPLOYEE_COST' | 'BILLING',
            effectiveFrom: String(r.effectiveFrom),
            amount: String(r.amount),
            currencyCode: String(r.currencyCode || '')
          }))
        );
      } else {
        setHourlyRows([]);
        setError('Failed to load member rate overrides');
      }

      if (orgResult.status === 'fulfilled') {
        const orgRes = orgResult.value;
        const orgData = orgRes?.data ?? orgRes;
        const orgList = Array.isArray(orgData) ? orgData : [];
        setOrgDefaultRows(
          orgList.map((r: Record<string, unknown>) => ({
            kind: r.kind as 'EMPLOYEE_COST' | 'BILLING',
            effectiveFrom: String(r.effectiveFrom),
            amount: String(r.amount),
            currencyCode: String(r.currencyCode || '')
          }))
        );
      } else {
        setOrgDefaultRows([]);
      }
    } catch (e: any) {
      setError(e.response?.data?.message || 'Failed to load hourly rates');
      setHourlyRows([]);
      setOrgDefaultRows([]);
    } finally {
      setHourlyLoading(false);
    }
  }, [organizationId, user?.id]);

  useEffect(() => {
    if (open && user?.id && tab === 2) {
      loadHourlyData();
    }
  }, [open, user?.id, tab, loadHourlyData]);

  const saveMembership = async () => {
    if (!user) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await api.putOrganizationMember(organizationId, user.id, {
        role,
        isActive: memberActive
      });
      setSuccess('Membership updated.');
      onSaved();
    } catch (e: any) {
      setError(e.response?.data?.message || 'Failed to update membership');
    } finally {
      setSaving(false);
    }
  };

  const openHourlyDialog = (kind: 'EMPLOYEE_COST' | 'BILLING', row: HourlyRow | null) => {
    setHourlyDialogKind(kind);
    if (row) {
      setHourlyEditId(row.id);
      setHourlyFormFrom(toDateTimeLocal(row.effectiveFrom));
      setHourlyFormAmount(row.amount);
      setHourlyFormCurrency(row.currencyCode.toUpperCase());
    } else {
      setHourlyEditId(null);
      setHourlyFormFrom(toDateTimeLocal(new Date().toISOString()));
      setHourlyFormAmount('');
      setHourlyFormCurrency('USD');
    }
    setHourlyDialogOpen(true);
  };

  const saveHourlyDialog = async () => {
    if (!user) return;
    const iso = fromDateTimeLocal(hourlyFormFrom);
    const code = hourlyFormCurrency.trim().toUpperCase();
    if (code.length !== 3 || !currencyCodes.includes(code)) {
      setError('Choose a valid ISO 4217 currency.');
      return;
    }
    if (!hourlyFormAmount.trim()) {
      setError('Enter an amount (use 0 if needed).');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (hourlyEditId) {
        await api.patchMemberHourlyRate(organizationId, user.id, hourlyEditId, {
          effectiveFrom: iso,
          amount: hourlyFormAmount.trim(),
          currencyCode: code
        });
      } else {
        await api.createMemberHourlyRate(organizationId, user.id, {
          kind: hourlyDialogKind,
          effectiveFrom: iso,
          amount: hourlyFormAmount.trim(),
          currencyCode: code
        });
      }
      setHourlyDialogOpen(false);
      await loadHourlyData();
      setSuccess('Rate row saved.');
    } catch (e: any) {
      setError(e.response?.data?.message || 'Failed to save rate');
    } finally {
      setSaving(false);
    }
  };

  const deleteHourlyRow = async (row: HourlyRow) => {
    if (!user) return;
    if (!window.confirm('Delete this override row?')) return;
    setSaving(true);
    setError(null);
    try {
      await api.deleteMemberHourlyRate(organizationId, user.id, row.id);
      await loadHourlyData();
      setSuccess('Rate row deleted.');
    } catch (e: any) {
      setError(e.response?.data?.message || 'Failed to delete');
    } finally {
      setSaving(false);
    }
  };

  const renderRateSection = (kind: 'EMPLOYEE_COST' | 'BILLING', title: string, hint: string) => {
    const rows = hourlyRows.filter((r) => r.kind === kind);
    const orgEffective = pickEffectiveOrgDefault(orgDefaultRows, kind, new Date());
    return (
      <Box sx={{ mb: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="subtitle1">{title}</Typography>
          <Button size="small" startIcon={<AddIcon />} onClick={() => openHourlyDialog(kind, null)} disabled={saving}>
            Add override
          </Button>
        </Box>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          {hint}
        </Typography>
        <Alert severity="info" sx={{ mb: 1 }}>
          <Typography variant="body2">
            <strong>Organization default</strong> (used when you have no matching override for the instant){' '}
            <strong>now</strong>:{' '}
            {orgEffective ? (
              <>
                {orgEffective.amount} {orgEffective.currencyCode}/h — effective from {formatDateTime(orgEffective.effectiveFrom)} (
                {userTzLabel})
              </>
            ) : (
              <>None — treated as <strong>0</strong> until the organization adds a default row.</>
            )}
          </Typography>
        </Alert>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Effective from</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>CCY</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Typography variant="body2" color="text.secondary">
                      No overrides — organization defaults apply.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{formatDateTime(row.effectiveFrom)}</TableCell>
                    <TableCell>{row.amount}</TableCell>
                    <TableCell>{row.currencyCode}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => openHourlyDialog(kind, row)} disabled={saving}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => deleteHourlyRow(row)} disabled={saving}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  if (!user) return null;

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="body">
        <DialogTitle>Edit member — {user.displayName || user.email}</DialogTitle>
        <DialogContent dividers sx={{ pt: 0 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2, mt: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2, mt: error ? 0 : 2 }} onClose={() => setSuccess(null)}>
              {success}
            </Alert>
          )}

          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Tab label="General" />
            <Tab label="Work calendar" />
            <Tab label="Rates" />
          </Tabs>

          {tab === 0 && (
            <Box>
              {user.displayName && (
                <TextField
                  label="Display name"
                  value={user.displayName}
                  fullWidth
                  size="small"
                  margin="normal"
                  InputProps={{ readOnly: true }}
                />
              )}
              <TextField
                label="Email"
                value={user.email}
                fullWidth
                size="small"
                margin="normal"
                InputProps={{ readOnly: true }}
              />
              <FormControl fullWidth size="small" sx={{ mt: 2, mb: 2 }}>
                <InputLabel>Role</InputLabel>
                <Select value={role} label="Role" onChange={(e) => setRole(e.target.value)}>
                  {availableRoles.map((r) => (
                    <MenuItem key={r} value={r}>
                      {r === 'MANAGER' ? 'Manager' : r === 'MEMBER' ? 'Member' : 'Owner'}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControlLabel
                control={<Switch checked={memberActive} onChange={(_, v) => setMemberActive(v)} disabled={saving} />}
                label="Active in this organization"
              />
              <Button variant="contained" sx={{ display: 'block', mt: 2 }} onClick={saveMembership} disabled={saving}>
                Save membership
              </Button>
            </Box>
          )}

          {tab === 1 && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Org calendar assignment and employment % are managed here. My work calendar adjustments apply on top;
                the member can also edit them in User preferences.
              </Typography>
              <UserWorkingCalendarPanel
                organizationId={organizationId}
                userId={user.id}
                allowOrgAdminEdits
              />
            </Box>
          )}

          {tab === 2 && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                These rows override organization default rates for this user only. Same effective-dating rules as
                organization settings → Default rates.
              </Typography>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  {timesShownInUtcOnly ? (
                    <>
                      Your <strong>User preferences</strong> use timezone UTC. Dates and times here match stored UTC
                      values.
                    </>
                  ) : (
                    <>
                      Times are <strong>stored in UTC</strong> and <strong>shown</strong> using your User preferences
                      (timezone <strong>{userTzLabel}</strong>, date format, 12/24h clock).
                    </>
                  )}
                </Typography>
              </Alert>
              {hourlyLoading ? (
                <LinearProgress />
              ) : (
                <>
                  {renderRateSection('EMPLOYEE_COST', 'Employee cost (per hour)', 'Internal cost override.')}
                  {renderRateSection('BILLING', 'Billing rate (per hour)', 'Client-facing rate override.')}
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={hourlyDialogOpen} onClose={() => setHourlyDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{hourlyEditId ? 'Edit override row' : 'Add override row'}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 1 }}>
            {hourlyDialogKind === 'EMPLOYEE_COST' ? 'Employee cost' : 'Billing rate'}
          </Typography>
          <TextField
            label="Effective from"
            type="datetime-local"
            value={hourlyFormFrom}
            onChange={(e) => setHourlyFormFrom(e.target.value)}
            fullWidth
            margin="normal"
            InputLabelProps={{ shrink: true }}
            disabled={saving}
            helperText={dateTimeFieldHelperText}
          />
          <TextField
            label="Amount per hour"
            value={hourlyFormAmount}
            onChange={(e) => setHourlyFormAmount(e.target.value)}
            fullWidth
            margin="normal"
            disabled={saving}
          />
          <Autocomplete
            options={currencyCodes}
            value={hourlyFormCurrency.length === 3 ? hourlyFormCurrency.toUpperCase() : null}
            onChange={(_, v) => setHourlyFormCurrency(v ? v.toUpperCase() : '')}
            getOptionLabel={(o) => formatCurrencyPickerLabel(o)}
            disabled={saving}
            sx={{ mt: 2 }}
            renderInput={(params) => <TextField {...params} label="Currency" />}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHourlyDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveHourlyDialog} disabled={saving}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default OrganizationMemberEditDialog;
